'use client';
import AddCartBlackIcon from '@/assets/icons/AddCartBlack.svg';
import CDNImage from '@/components/cdnImage/cdnImage';
import JDIPrice from '@/components/price';
import { comparePrice, getExptag } from '@/components/productCard/common/utils';
import type { productData_2 } from '@/components/productCard/index.interface';
import { useTranslation } from '@/i18n/client';
import { formatProductLink } from '@/utils';
import pageSDK from '@/utils/goToPage';
import style from './productCard.module.scss';
import { addCartUtil } from '@/utils/cartUtils';
import { useStores } from '@/models';
import { CartData } from '@/apis/cart/cart.interface';

interface PropsType {
  productData: productData_2;
}

export default function ProductCard_2({ productData }: PropsType) {
  const { t } = useTranslation();
  const { cartNumStore } = useStores();

  const handleProductClick = (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    if (trackingPointParams && trackingPointParams.productClickPoint) {
      trackingPointParams.productClickPoint();
    }

    if (onClickProduct && typeof onClickProduct === 'function') {
      onClickProduct(productData);
    } else {
      pageSDK.goToProduct({ skuId: skuId });
    }
  };

  //优惠价格的默认样式配置
  const defaultRealPriceJDIPriceParams = {
    symbolStyle: { fontSize: '16px' },
    integerStyle: { fontSize: '24px' },
    decimalPointStyle: { fontSize: '16px' },
    decimalPlaceStyle: { fontSize: '16px' },
    containerStyle: { display: 'block', height: '24px', lineHeight: '24px' },
  };

  //原价的默认样式配置
  const defaultOriginalPriceJDIPriceParams = {
    containerStyle: { height: '12px', lineHeight: '12px', fontSize: '12px' },
    uiType: 2,
  };

  let {
    width, // 卡片宽度
    height, // 卡片高度
    containerStyle, // 容器样式
    contentStyle, // 内容区域样式
    skuName, // 商品名称
    skuNameStyle, // 商品名称样式
    skuImgKeyParams, // 商品图片参数
    skuImg, // 商品图片URL
    skuImgStyle, // 商品图片样式
    skuId, // 商品SKU ID
    realPrice, // 优惠价格
    // @ts-ignore
    realPriceJDIPriceParams, // 优惠价格组件参数
    originalPrice, // 原价
    originalPriceJDIPriceParams, // 原价组件参数
    currency,
    languageCode,
    shouldShowCart, // 是否显示购物车图标
    cartIconStyle, // 购物车图标样式
    // @ts-ignore
    itemType = 1, // 商品类型，默认为1
    onClickCart, // 购物车点击事件处理函数
    // @ts-ignore
    cartChange, // 购物车变化回调函数
    onClickProduct, // 商品点击事件处理函数
    isOutOfStock, // 是否缺货
    isRemoved, // 是否已下架
    priceStyle, // 价格区域样式
    saleInfo, // 促销信息
    saleInfoStyle, // 促销信息样式
    trackingPointParams, // 埋点统计参数
  } = productData;

  // 合并容器样式，设置宽度和高度
  containerStyle = Object.assign({}, containerStyle, {
    width,
    height,
  });

  // @ts-ignore
  const handleCartClick = async (e: any, productData: productData_2) => {
    e.preventDefault();
    e.stopPropagation();

    if (trackingPointParams && trackingPointParams.cartClickPoint) {
      trackingPointParams.cartClickPoint();
    }

    if (onClickCart && typeof onClickCart === 'function') {
      onClickCart(productData);
    } else {
      gotoCart(productData.skuId);
    }
  };

  const gotoCart = (targetSkuId: string) => {
    addCart([
      {
        skuId: targetSkuId ? targetSkuId : productData.skuId, // 商品SKU ID
        num: '1',
      },
    ]);
  };

  /**
   * 调用加车接口
   * 参考文档：https://cf.jd.com/pages/viewpage.action?pageId=605887939
   *
   * @param params - 加车参数
   */
  const addCart = async (params: any) => {
    const res = await addCartUtil({
      productList: params,
    });
    const result = res?.result;
    if (result?.code === '200') {
      if (trackingPointParams && trackingPointParams.addCartPoint) {
        trackingPointParams.addCartPoint();
      }
      cartNumStore.updateCartNum(result.data as CartData);
      // 执行购物车变化回调
      if (cartChange && typeof cartChange === 'function') {
        cartChange(productData);
      } else {
        console.log('H5没有加购回调fun');
      }
    }
  };

  const isShowOriginalPrice = originalPrice && comparePrice(originalPrice, realPrice);

  return (
    <div
      className={style.UK_product_card}
      style={containerStyle}
      key={skuId}
      // 埋点实验标签配置
      exptag={getExptag('exptag', trackingPointParams, 'productExpConfig')}
      data-exptag-config={getExptag('config', trackingPointParams, 'productExpConfig')}
      data-exptag-json-param={getExptag('json-param', trackingPointParams, 'productExpConfig')}
      data-exptag-event-param={getExptag('event-param', trackingPointParams, 'productExpConfig')}
    >
      {/* 商品链接区域 */}
      <a href={formatProductLink(skuId, skuName)} onClick={(e) => handleProductClick(e)}>
        {/* 商品图片容器 */}
        <div className={style.UK_image_wrapper}>
          <div className={style.UK_image} style={skuImgStyle}>
            {/* 商品图片 */}
            <CDNImage
              className={`${style.UK_skuImg} ${isOutOfStock || isRemoved ? style.op50 : ''}`}
              src={(skuImgKeyParams ? skuImgKeyParams?.imgUrlKey : skuImg) as string}
              width={skuImgKeyParams?.width || 140}
              height={skuImgKeyParams?.height || 140}
              alt={skuName}
            />

            {/* 商品状态标签 */}
            {isRemoved ? (
              // 已下架状态
              <div className={style.UK_outOfStock}>
                <div className={style.UK_outOfStock_text}>{t('common.productStatus.removed')}</div>
              </div>
            ) : isOutOfStock ? (
              // 缺货状态
              <div className={style.UK_outOfStock}>
                <div className={style.UK_outOfStock_text}>
                  {t('common.productStatus.outOfStock')}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </a>

      {/* 商品内容区域 */}
      <div className={style.UK_product_content} style={contentStyle}>
        {/* 商品名称区域 */}
        <div className={style.UK_name_box} onClick={(e) => handleProductClick(e)}>
          <span className={style.UK_name} style={skuNameStyle}>
            {skuName}
          </span>
        </div>

        {/* 促销信息展示 */}
        {saleInfo && (
          <div className={style.UK_saleInfo} style={saleInfoStyle}>
            {saleInfo}
          </div>
        )}

        {/* 价格区域和购物车图标 */}
        <div className={style.UK_price_and_cart_wrapper}>
          {/* 左侧价格区域 */}
          <div className={style.UK_left} style={priceStyle}>
            {/* 优惠价格 */}
            <JDIPrice
              {...defaultRealPriceJDIPriceParams}
              {...realPriceJDIPriceParams}
              currency={currency}
              locale={languageCode}
              price={realPrice}
              uiType={1}
            />

            {/* 划线原价（当原价高于优惠价格时显示） */}
            {isShowOriginalPrice && (
              <div className={style.UK_originalPrice}>
                <JDIPrice
                  {...defaultOriginalPriceJDIPriceParams}
                  {...originalPriceJDIPriceParams}
                  currency={currency}
                  locale={languageCode}
                  price={originalPrice}
                  uiType={2}
                  lineThrough
                />
              </div>
            )}
          </div>

          {/* 右侧购物车图标 */}
          {shouldShowCart && !isRemoved ? (
            <div
              className={style.UK_right}
              // 购物车埋点实验标签配置
              exptag={getExptag('exptag', trackingPointParams, 'cartExpConfig')}
              data-exptag-config={getExptag('config', trackingPointParams, 'cartExpConfig')}
              data-exptag-json-param={getExptag('json-param', trackingPointParams, 'cartExpConfig')}
              data-exptag-event-param={getExptag(
                'event-param',
                trackingPointParams,
                'cartExpConfig',
              )}
            >
              <AddCartBlackIcon
                style={cartIconStyle}
                className={style.UK_cart}
                onClick={(e: any) => handleCartClick(e, productData)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
